// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@tps/App/Components/Common/XngZone";
import {
    FooterBottomSection,
    FooterContainerWrapper,
    FooterTopSection,
    GeneralLinksWrapper,
} from "@tps/App/WidgetExtensions/Footer/FooterContainerExtensions";
import { checkoutPages } from "@tps/XDL/constants";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    // XNG-CHANGES
    bottomSectionStyledWrapper?: InjectableCss;
    copyRightWrapper?: InjectableCss;
    termsWrapper?: InjectableCss;
    topSectionInnerPage?: MobiusPageProps;
    bottomSectionInnerPage?: MobiusPageProps;
    companiesLinksWrapper?: InjectableCss;
    copyRightAndTermsWrapper?: InjectableCss;
}

export const footerContainerStyles: FooterContainerStyles = {
    logoGridItem: {
        width: 12,
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            img {
                height: 78px;
            }
        `,
    },
    linkList1GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList2GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList3GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList4GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    subscribeGridItem: {
        width: [12, 12, 8, 4, 4],
    },
    socialLinksGridItem: {
        width: 12,
    },
    siteMapLinksGridItem: {
        width: 12,
    },
    richContentGridItem: {
        width: 12,
    },
    logoWrapper: {
        css: css`
            width: 110px;
        `,
    },
};

const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));

    // <XNG-Changes>
    const { isCheckoutPage } = useAppSelector(state => ({
        isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type) || getCurrentPage(state).type === "OrderConfirmationPage",
    }));
    const copyrightMessage = () => {
        const message = siteMessage("Custom_Footer_Copyright") as string;
        return message.replace("{0}", String(new Date().getFullYear()));
    };
    // </XNG-Changes>

    // XNG-Change: changes on structure to match design
    return (
        <FooterContainerWrapper>
            {!isCheckoutPage && (
                <FooterTopSection>
                    <Page as="div" {...styles.topSectionInnerPage}>
                        <GeneralLinksWrapper>
                            <Zone zoneName="LinkList1" contentId={id} />
                        </GeneralLinksWrapper>
                        <GeneralLinksWrapper>
                            <Zone zoneName="LinkList2" contentId={id} />
                        </GeneralLinksWrapper>
                        <GeneralLinksWrapper>
                            <Zone zoneName="LinkList3" contentId={id} />
                        </GeneralLinksWrapper>
                        <GeneralLinksWrapper>
                            <Zone zoneName="LinkList4" contentId={id} />
                        </GeneralLinksWrapper>
                        <GeneralLinksWrapper>
                            <XngZone hint="Social Icons" contentId={id} zoneName="Social Icons" />
                        </GeneralLinksWrapper>
                    </Page>
                </FooterTopSection>
            )}
            <FooterBottomSection {...{ isCheckoutPage }}>
                <Page as="div" {...styles.bottomSectionInnerPage} {...{ isCheckoutPage }}>
                    {!isCheckoutPage && (
                        <StyledWrapper {...styles.companiesLinksWrapper}>
                            <XngZone hint="TPS Family of Companies" contentId={id} zoneName="Links" />
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.copyRightAndTermsWrapper} {...{ isCheckoutPage }}>
                        <StyledWrapper {...styles.copyRightWrapper}>{copyrightMessage()}</StyledWrapper>
                        <StyledWrapper {...styles.termsWrapper} {...{ isCheckoutPage }} className="terms">
                            <XngZone hint="Terms & conditions & privacy policy" contentId={id} zoneName="Terms" />
                        </StyledWrapper>
                    </StyledWrapper>
                </Page>
            </FooterBottomSection>
        </FooterContainerWrapper>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
